import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, onSnapshot, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import './Journal.css';

const Journal = () => {
    const [user, setUser] = useState(null);
    const [coupleData, setCoupleData] = useState({ journalEntries: [] });
    const [newEntry, setNewEntry] = useState('');
    const [coupleId, setCoupleId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubAuth = auth.onAuthStateChanged(async (currentUser) => {
            if (currentUser) {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    setUser(userData);
                    if (userData.partnerId) {
                        const ids = [currentUser.uid, userData.partnerId].sort();
                        setCoupleId(ids.join('_'));
                    } else {
                        setLoading(false);
                    }
                }
            } else {
                setUser(null);
                setCoupleId(null);
                setLoading(false);
            }
        });
        return () => unsubAuth();
    }, []);

    useEffect(() => {
        if (!coupleId) return;
        const coupleDocRef = doc(db, 'coupleData', coupleId);
        const unsubscribe = onSnapshot(coupleDocRef, (docSnap) => {
            if (docSnap.exists()) {
                setCoupleData(docSnap.data());
            } else {
                // If the document doesn't exist, we can create it.
                // For now, we'll just assume it exists or will be created by another feature.
                setCoupleData({ journalEntries: [] });
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [coupleId]);

    const handleAddEntry = async (e) => {
        e.preventDefault();
        if (newEntry.trim() === '' || !coupleId || !user) return;

        const entry = {
            id: `entry_${Date.now()}`,
            text: newEntry,
            authorId: auth.currentUser.uid,
            authorNickname: user.nickname,
            createdAt: new Date().toISOString(),
        };

        const coupleDocRef = doc(db, 'coupleData', coupleId);
        await updateDoc(coupleDocRef, {
            journalEntries: arrayUnion(entry)
        }, { merge: true });

        setNewEntry('');
    };
    
    if (loading) {
        return <div className="journal-container">Loading Journal...</div>;
    }

    if (!user?.partnerId) {
        return <div className="journal-container"><p>Please link with a partner to start a shared journal.</p></div>;
    }

    // Sort entries by date, newest first
    const sortedEntries = coupleData.journalEntries ? [...coupleData.journalEntries].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];

    return (
        <div className="journal-container">
            <h1>Our Journal</h1>
            <form onSubmit={handleAddEntry} className="journal-form">
                <textarea
                    value={newEntry}
                    onChange={(e) => setNewEntry(e.target.value)}
                    placeholder="What's on your mind?"
                    rows="4"
                    required
                />
                <button type="submit">Add Entry</button>
            </form>
            <div className="journal-entries-list">
                {sortedEntries.length > 0 ? (
                    sortedEntries.map(entry => (
                        <div key={entry.id} className={`journal-entry ${entry.authorId === auth.currentUser.uid ? 'yours' : 'theirs'}`}>
                            <p className="entry-text">{entry.text}</p>
                            <div className="entry-meta">
                                <span className="entry-author">{entry.authorNickname}</span>
                                <span className="entry-date">{new Date(entry.createdAt).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No entries yet. Be the first to write!</p>
                )}
            </div>
        </div>
    );
};

export default Journal;
